import React from 'react';
import { Row, Col, Select, Input, InputNumber, DatePicker, Switch, Button, Space } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { QueryCondition, FieldConfig } from './types';
import { getFieldOperators, getOperatorValueType } from './utils';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface QueryConditionItemProps {
  condition: QueryCondition;
  fields: FieldConfig[];
  onChange: (condition: QueryCondition) => void;
  onRemove: () => void;
  showLogic?: boolean;
}

const QueryConditionItem: React.FC<QueryConditionItemProps> = ({
  condition,
  fields,
  onChange,
  onRemove,
  showLogic = true
}) => {
  const fieldConfig = fields.find(f => f.key === condition.field);
  const operators = fieldConfig ? getFieldOperators(fieldConfig) : [];
  const valueType = getOperatorValueType(condition.operator);

  const handleFieldChange = (field: string) => {
    const newFieldConfig = fields.find(f => f.key === field);
    const defaultOperator = newFieldConfig?.operators?.[0] || 'eq';
    
    onChange({
      ...condition,
      field,
      operator: defaultOperator,
      value: undefined
    });
  };

  const handleOperatorChange = (operator: string) => {
    onChange({
      ...condition,
      operator,
      value: undefined
    });
  };

  const handleValueChange = (value: any) => {
    onChange({
      ...condition,
      value
    });
  };

  const handleLogicChange = (logic: 'and' | 'or') => {
    onChange({
      ...condition,
      logic
    });
  };

  const renderValueInput = () => {
    if (!fieldConfig) return null;

    switch (valueType) {
      case 'none':
        return null;

      case 'boolean':
        return (
          <Switch
            checked={condition.value}
            onChange={handleValueChange}
          />
        );

      case 'array':
        if (fieldConfig.type === 'select' || fieldConfig.type === 'multiSelect') {
          return (
            <Select
              mode="multiple"
              placeholder={fieldConfig.placeholder || '请选择'}
              value={condition.value}
              onChange={handleValueChange}
              style={{ minWidth: 200 }}
            >
              {fieldConfig.options?.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          );
        }
        return (
          <Select
            mode="tags"
            placeholder={fieldConfig.placeholder || '请输入，回车分隔'}
            value={condition.value}
            onChange={handleValueChange}
            style={{ minWidth: 200 }}
          />
        );

      case 'range':
        if (fieldConfig.type === 'date') {
          return (
            <RangePicker
              value={condition.value}
              onChange={handleValueChange}
              format={fieldConfig.format || 'YYYY-MM-DD'}
              placeholder={Array.isArray(fieldConfig.placeholder) ? fieldConfig.placeholder : undefined}
            />
          );
        }
        if (fieldConfig.type === 'number') {
          return (
            <Space.Compact>
              <InputNumber
                placeholder="最小值"
                value={condition.value?.[0]}
                onChange={(val) => handleValueChange([val, condition.value?.[1]])}
              />
              <InputNumber
                placeholder="最大值"
                value={condition.value?.[1]}
                onChange={(val) => handleValueChange([condition.value?.[0], val])}
              />
            </Space.Compact>
          );
        }
        break;

      case 'single':
      default:
        switch (fieldConfig.type) {
          case 'number':
            return (
              <InputNumber
                placeholder={fieldConfig.placeholder as any}
                value={condition.value}
                onChange={handleValueChange}
                style={{ width: 200 }}
              />
            );

          case 'date':
            return (
              <DatePicker
                value={condition.value ? dayjs(condition.value) : undefined}
                onChange={handleValueChange}
                format={fieldConfig.format || 'YYYY-MM-DD'}
              />
            );

          case 'boolean':
            return (
              <Select
                placeholder="请选择"
                value={condition.value}
                onChange={handleValueChange}
                style={{ width: 100 }}
              >
                <Option value={true}>是</Option>
                <Option value={false}>否</Option>
              </Select>
            );

          case 'select':
            return (
              <Select
                placeholder={fieldConfig.placeholder || '请选择'}
                value={condition.value}
                onChange={handleValueChange}
                style={{ minWidth: 150 }}
                allowClear
              >
                {fieldConfig.options?.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            );

          case 'string':
          default:
            return (
              <Input
                placeholder={fieldConfig.placeholder as any}
                value={condition.value}
                onChange={(e) => handleValueChange(e.target.value)}
                style={{ width: 200 }}
              />
            );
        }
    }

    return null;
  };

  return (
    <Row gutter={8} align="middle" style={{ marginBottom: 8 }}>
      {showLogic && (
        <Col>
          <Select
            value={condition.logic || 'and'}
            onChange={handleLogicChange}
            style={{ width: 60 }}
          >
            <Option value="and">且</Option>
            <Option value="or">或</Option>
          </Select>
        </Col>
      )}
      
      <Col>
        <Select
          placeholder="选择字段"
          value={condition.field}
          onChange={handleFieldChange}
          style={{ width: 150 }}
        >
          {fields.map(field => (
            <Option key={field.key} value={field.key}>
              {field.label}
            </Option>
          ))}
        </Select>
      </Col>

      <Col>
        <Select
          placeholder="选择操作符"
          value={condition.operator}
          onChange={handleOperatorChange}
          style={{ width: 120 }}
        >
          {operators.map(op => (
            <Option key={op.value} value={op.value} title={op.description}>
              {op.label}
            </Option>
          ))}
        </Select>
      </Col>

      <Col flex="auto">
        {renderValueInput()}
      </Col>

      <Col>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={onRemove}
          size="small"
        />
      </Col>
    </Row>
  );
};

export default QueryConditionItem;
